import React, { useState, useEffect } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import AuthBackground from '../components/AuthBackground';

const getLoginErrorMessage = (error: any): string => {
  const status = error?.response?.status;
  const serverMessage = error?.response?.data?.message?.toLowerCase() || '';
  if (status === 401) {
    if (serverMessage.includes('bad credentials') || serverMessage.includes('invalid')) return 'Incorrect username or password. Please try again.';
    if (serverMessage.includes('locked') || serverMessage.includes('disabled')) return 'Your account has been locked. Please contact support.';
    return 'Invalid credentials. Please check your username and password.';
  }
  if (status === 403) return 'Access denied. You do not have permission to login here.';
  if (status === 404) return 'Account not found. Please check your username or sign up.';
  if (status === 429) return 'Too many login attempts. Please try again in a few minutes.';
  if (status === 500 || status === 502 || status === 503) return 'Server error. Please try again later or contact support.';
  if (status === 0 || !status) return 'Unable to connect to server. Please check your internet connection.';
  if (serverMessage.includes('network') || serverMessage.includes('timeout')) return 'Connection timeout. Please check your internet connection and try again.';
  if (serverMessage.includes('email') && serverMessage.includes('verified')) return 'Please verify your email address before logging in.';
  return 'Login failed. Please check your credentials and try again.';
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2"/>
  </svg>
);

const Login: React.FC = () => {
  const { signin, token, user } = useAuth();
  const navigate = useNavigate();
  const { theme, currentThemeName } = useTheme();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const isModernTheme = currentThemeName === 'modernDashboard';
  // saffron (the current default) uses the same split-hero login as the warm preset
  const isWarmTheme = currentThemeName === 'scandinavianWarm' || currentThemeName === 'saffron';

  useEffect(() => {
    if (token && user) {
      // Finance-only steward: drop them straight onto Income (which they have
      // permission to add to). The bottom nav + FinanceTabBar both filter to
      // the three allowed entries (Income / Expenses / EOD) for this user.
      // Must come BEFORE any role-based default so a restricted steward never
      // lands on /steward/tables.
      if (user.financeOnlySteward) {
        navigate('/vendor/finance/income', { replace: true });
        return;
      }
      // Optional per-user landing override (e.g. ranmoor bawarchi → /vendor/finance).
      // Applies only when explicitly set by the backend.
      if (user.defaultLandingPath) {
        navigate(user.defaultLandingPath, { replace: true });
        return;
      }
      if (user.roles.includes('ROLE_ADMIN')) navigate('/admin/orders', { replace: true });
      else if (user.roles.includes('ROLE_VENDOR')) {
        navigate(isModernTheme ? '/vendor/dashboard' : '/vendor/orders', { replace: true });
      }
      else if (user.roles.includes('ROLE_STEWARD')) navigate('/steward/tables', { replace: true });
      else if (user.roles.includes('ROLE_USER')) {
        navigate(isModernTheme ? '/customer/dashboard' : '/qr', { replace: true });
      }
    }
  }, [token, user, navigate, isModernTheme]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signin(username, password);
    } catch (err) {
      setError(getLoginErrorMessage(err));
      setLoading(false);
    }
  };

  const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';

  // ─── Scandinavian Warm ────────────────────────────────────────────────
  if (isWarmTheme) {
    // Shared form markup used in both mobile (no card) and desktop (right panel)
    const warmForm = (
      <>
        <form onSubmit={handleSubmit}>
          {/* Username */}
          <div className="mb-4">
            <label htmlFor="w-username" className="block text-[13px] font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
              Username
            </label>
            <input
              id="w-username"
              type="text"
              autoComplete="username"
              required
              placeholder="Enter your username"
              className="w-full px-3.5 py-[11px] text-[14px] rounded-lg transition-all duration-150 focus:outline-none"
              style={{ backgroundColor: 'var(--bg-sunken)', border: '1px solid var(--border-default)', color: 'var(--text-primary)' }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--action)'; e.currentTarget.style.boxShadow = '0 0 0 3px color-mix(in srgb, var(--focus-ring) 15%, transparent)'; e.currentTarget.style.backgroundColor = 'var(--bg-surface)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.backgroundColor = 'var(--bg-sunken)'; }}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Password */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-1.5">
              <label htmlFor="w-password" className="block text-[13px] font-medium" style={{ color: 'var(--text-secondary)' }}>
                Password
              </label>
              <a href="/forgot-password" className="text-[12px] font-medium hover:underline" style={{ color: 'var(--action)' }}>
                Forgot password?
              </a>
            </div>
            <div className="relative">
              <input
                id="w-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                placeholder="Enter your password"
                className="w-full px-3.5 py-[11px] pr-10 text-[14px] rounded-lg transition-all duration-150 focus:outline-none"
                style={{ backgroundColor: 'var(--bg-sunken)', border: '1px solid var(--border-default)', color: 'var(--text-primary)' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--action)'; e.currentTarget.style.boxShadow = '0 0 0 3px color-mix(in srgb, var(--focus-ring) 15%, transparent)'; e.currentTarget.style.backgroundColor = 'var(--bg-surface)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.backgroundColor = 'var(--bg-sunken)'; }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5"
                style={{ color: '#B0ADA6', background: 'none', border: 'none' }} /* basil-todo */
                onMouseEnter={(e) => { e.currentTarget.style.color = '#777'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#B0ADA6'; }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeSlashIcon className="w-[17px] h-[17px]" /> : <EyeIcon className="w-[17px] h-[17px]" />}
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div role="alert" className="flex items-start gap-2 p-3 text-[13px] rounded-lg mb-4" style={{ backgroundColor: 'var(--status-bad-bg)', border: '1px solid var(--status-bad-border)', color: 'var(--status-bad-text)' }}>
              <svg className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--status-bad)' }} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd"/>
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-[11px] rounded-lg text-[14px] font-semibold flex items-center justify-center disabled:opacity-50 transition-all duration-150"
            style={{ backgroundColor: 'var(--action)', color: 'var(--text-inverse)', boxShadow: '0 1px 3px color-mix(in srgb, var(--action) 30%, transparent)' }}
            onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.backgroundColor = 'var(--action-hover)'; e.currentTarget.style.boxShadow = '0 2px 8px color-mix(in srgb, var(--action) 40%, transparent)'; }}}
            onMouseLeave={(e) => { if (!loading) { e.currentTarget.style.backgroundColor = 'var(--action)'; e.currentTarget.style.boxShadow = '0 1px 3px color-mix(in srgb, var(--action) 30%, transparent)'; }}}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                Signing in...
              </span>
            ) : 'Sign in'}
          </button>
        </form>

        {/* Social login (Google + Facebook) hidden until OAuth2 redirect URIs
            and client IDs are configured per environment. Toggle SHOW_SOCIAL
            to re-enable once those are wired. */}
        {false && (
          <>
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px" style={{ backgroundColor: 'var(--bg-sunken)' }} />
              <span className="text-[11px] font-medium" style={{ color: '#C0BDB7' }}>or</span> {/* basil-todo */}
              <div className="flex-1 h-px" style={{ backgroundColor: 'var(--bg-sunken)' }} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <a
                href={`${apiBase}/oauth2/authorization/google`}
                className="flex items-center justify-center gap-2 py-[9px] text-[13px] font-medium rounded-lg transition-all duration-150"
                style={{ border: '1px solid #E2E0DB', color: 'var(--text-secondary)' }} /* basil-todo */
              >
                <GoogleIcon /><span>Google</span>
              </a>
              <a
                href={`${apiBase}/oauth2/authorization/facebook`}
                className="flex items-center justify-center gap-2 py-[9px] text-[13px] font-medium rounded-lg transition-all duration-150"
                style={{ border: '1px solid #E2E0DB', color: 'var(--text-secondary)' }} /* basil-todo */
              >
                <FacebookIcon /><span>Facebook</span>
              </a>
            </div>
          </>
        )}

        {/* Signup link */}
        <p className="text-center text-[13px] mt-6" style={{ color: 'var(--text-tertiary)' }}>
          Don't have an account?{' '}
          <a href="/signup" className="font-semibold hover:underline" style={{ color: 'var(--action)' }}>Create one</a>
        </p>
      </>
    );

    return (
      <>
        {/* Loading overlay */}
        {loading && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(4px)' }}>
            <div className="rounded-xl p-6 flex flex-col items-center" style={{ backgroundColor: 'var(--bg-surface)', boxShadow: '0 20px 40px rgba(0,0,0,0.12)' }}>
              <div className="animate-spin rounded-full h-8 w-8 border-2" style={{ borderColor: '#E2E0DB', borderTopColor: 'var(--action)' }} /> {/* basil-todo */}
              <p className="mt-3 text-[13px] font-medium" style={{ color: 'var(--text-tertiary)' }}>Signing you in...</p>
            </div>
          </div>
        )}

        <div className="fixed inset-0 z-50 flex">
          {/* ── Desktop: Left brand panel ── */}
          <div
            className="hidden lg:flex lg:w-[42%] xl:w-[38%] flex-col relative overflow-hidden"
            style={{ background: 'linear-gradient(165deg, #3A2540 0%, #241826 55%, #140D16 100%)' /* brand hero: aubergine ramp, matches --band-bg family */ }}
          >
            {/* Decorative */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute" style={{ width: 500, height: 500, top: '-15%', right: '-20%', borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 60%)' }} />
              <div className="absolute" style={{ width: 300, height: 300, bottom: '10%', left: '-10%', borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 60%)' }} />
            </div>

            <div className="relative z-10 flex flex-col justify-between h-full p-10 xl:p-12">
              {/* Logo */}
              <div>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                    <img src="/favicon-64.png" alt="" className="w-5 h-5 brightness-0 invert opacity-70" />
                  </div>
                  <span className="text-white/70 text-[13px] font-medium tracking-wide">Scan Order & Go</span>
                </div>
              </div>

              {/* Tagline */}
              <div>
                <h2 className="text-white text-[2.2rem] xl:text-[2.6rem] font-bold leading-[1.15]" style={{ fontFamily: 'var(--font-family-heading, Fraunces, serif)' }}>
                  Your restaurant,<br />
                  <span className="text-white/60">one platform.</span>
                </h2>
                <p className="text-white/40 text-[14px] leading-relaxed mt-4 max-w-[300px]">
                  Orders, kitchen, floor layout, and reports — all from one dashboard.
                </p>
              </div>

              {/* Footer */}
              <div className="text-[11px] text-white/25">
                Powered by Nextserve
              </div>
            </div>
          </div>

          {/* ── Right / Main form area ── */}
          <div className="flex-1 flex items-center justify-center overflow-y-auto" style={{ backgroundColor: 'var(--bg-surface)' }}>
            <div className="w-full max-w-[380px] px-6 sm:px-8 py-10">
              {/* Logo — mobile only */}
              <div className="lg:hidden flex items-center gap-2 mb-10">
                <img src="/favicon-64.png" alt="" className="w-7 h-7 rounded opacity-60" />
                <span className="text-[13px] font-medium" style={{ color: 'var(--text-tertiary)' }}>Scan Order & Go</span>
              </div>

              {/* Heading */}
              <h1 className="text-[24px] font-bold mb-1" style={{ fontFamily: 'var(--font-family-heading, Fraunces, serif)', color: 'var(--text-primary)' }}>
                Welcome back
              </h1>
              <p className="text-[13px] mb-8" style={{ color: 'var(--text-tertiary)' }}>
                Sign in to continue to your dashboard
              </p>

              {warmForm}

              {/* Powered by — mobile only */}
              <div className="lg:hidden mt-10 text-center text-[11px]" style={{ color: '#D0CCC4' }}> {/* basil-todo */}
                Powered by Nextserve
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ─── Default layout (all other themes) ─────────────────────────────────
  return (
    <>
      {loading && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-[3px]" style={{ borderColor: `${theme.primary}33`, borderTopColor: theme.primary }} />
            <p className="mt-4 text-sm font-medium text-gray-700">Signing you in...</p>
          </div>
        </div>
      )}

      <AuthBackground>
        <div className="relative w-full max-w-[420px] mx-4 my-8">
          <div className="rounded-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', boxShadow: '0 25px 60px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1)' }}>
            <div className="pt-8 pb-1 px-8 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4" style={{ background: `linear-gradient(135deg, ${theme.button.primary.solidBg || theme.button.primary.bg} 0%, ${theme.button.primary.hover} 100%)`, boxShadow: `0 4px 14px -2px ${theme.primary}66` }}>
                <span className="text-white font-bold text-lg tracking-wider">SOG</span>
              </div>
              <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-family-heading)', color: theme.text }}>Welcome</h1>
              <p className="text-sm text-gray-500 mt-1">Sign in to your account to continue</p>
            </div>
            <div className="px-8 pb-6 pt-5">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1.5">Username</label>
                  <input id="username" type="text" autoComplete="username" required placeholder="Enter your username" className="appearance-none block w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent focus:bg-white transition-all" value={username} onChange={(e) => setUsername(e.target.value)} disabled={loading} />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                    <a href="/forgot-password" className="text-xs font-medium" style={{ color: theme.primary }}>Forgot password?</a>
                  </div>
                  <div className="relative">
                    <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required placeholder="Enter your password" className="appearance-none block w-full px-4 py-2.5 pr-11 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent focus:bg-white transition-all" value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} />
                    <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <EyeSlashIcon className="h-[17px] w-[17px]" /> : <EyeIcon className="h-[17px] w-[17px]" />}
                    </button>
                  </div>
                </div>
                {error && (
                  <div role="alert" className="flex items-start gap-2 p-3 text-sm bg-red-50 border border-red-200 text-red-700 rounded-xl">
                    <svg className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd"/></svg>
                    <span>{error}</span>
                  </div>
                )}
                <button type="submit" disabled={loading} className="w-full py-3 px-4 rounded-xl font-semibold text-sm flex items-center justify-center disabled:opacity-50 transition-all" style={{ background: `linear-gradient(135deg, ${theme.button.primary.solidBg || theme.button.primary.bg} 0%, ${theme.button.primary.hover} 100%)`, color: theme.button.primary.text, boxShadow: `0 4px 14px -2px ${theme.primary}66` }}>
                  {loading ? (<span className="flex items-center gap-2"><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Signing In...</span>) : 'Sign In'}
                </button>
              </form>
              {/* Social login hidden — see top of file. */}
              {false && (
                <>
                  <div className="relative my-6"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div><div className="relative flex justify-center"><span className="px-3 text-xs font-medium text-gray-400 uppercase tracking-wider" style={{ backgroundColor: 'rgba(255,255,255,0.97)' }}>or continue with</span></div></div>
                  <div className="grid grid-cols-2 gap-3">
                    <a href={`${apiBase}/oauth2/authorization/google`} className="flex items-center justify-center gap-2 py-2.5 px-4 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"><GoogleIcon /><span>Google</span></a>
                    <a href={`${apiBase}/oauth2/authorization/facebook`} className="flex items-center justify-center gap-2 py-2.5 px-4 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"><FacebookIcon /><span>Facebook</span></a>
                  </div>
                </>
              )}
              <p className="text-center text-sm text-gray-500 mt-6">Don't have an account?{' '}<a href="/signup" className="font-semibold" style={{ color: theme.primary }}>Create one</a></p>
            </div>
            <div className="px-8 pb-6"><div className="flex items-center justify-center gap-1.5 text-xs text-gray-400"><span>Powered by</span><img src="/favicon-64.png" alt="Nextserve" className="h-4 w-4 rounded-sm" /><span className="font-medium text-gray-500">Nextserve</span></div></div>
          </div>
        </div>
      </AuthBackground>
    </>
  );
};

export default Login;
